import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';

import {
  completeTrackedWorkItemMutation,
  isCompleteWorkItem,
  mergeWorkItemSnapshot,
  reconcileCreatedWorkItem,
  reconcileCompleteWorkItem,
} from '@/apps/dispatcher/redux/workItemReconciliation';
import type { WorkItemListScope } from '@/apps/dispatcher/redux/workItemReconciliation';
import type { WorkItemState } from '@/apps/dispatcher/redux/slices/workItemSlice';
import type { IWorkItem } from '@/apps/dispatcher/types/IWorkItem';
import { WorkItemStatus } from '@/apps/dispatcher/types/enums';

type WorkItemCaseReducer<Payload> = CaseReducer<
  WorkItemState,
  PayloadAction<Payload>
>;

export const beginWorkItemsFetchReducer: WorkItemCaseReducer<{
  requestId: number;
  scope: WorkItemListScope;
}> = (state, action) => {
  const { requestId, scope } = action.payload;
  delete state.loadedScopes[scope.status];
  state.activeFetches[scope.status] = {
    requestId,
    scope,
    touchedIds: [],
  };
};

export const completeWorkItemsFetchReducer: WorkItemCaseReducer<{
  requestId: number;
  scope: WorkItemListScope;
  workItems: IWorkItem[];
}> = (state, action) => {
  const { requestId, scope, workItems } = action.payload;
  const activeFetch = state.activeFetches[scope.status];
  if (activeFetch?.requestId !== requestId) {
    return;
  }

  state.workItems = mergeWorkItemSnapshot(
    state.workItems,
    workItems,
    scope,
    activeFetch.touchedIds
  );
  state.loadedScopes[scope.status] = scope;
  delete state.activeFetches[scope.status];
  state.loading = false;
  state.workItemUpdating = null;
  state.error = null;
};

export const failWorkItemsFetchReducer: WorkItemCaseReducer<{
  requestId: number;
  status: WorkItemStatus;
}> = (state, action) => {
  const { requestId, status } = action.payload;
  if (state.activeFetches[status]?.requestId === requestId) {
    delete state.activeFetches[status];
  }
};

export const beginWorkItemMutationReducer: WorkItemCaseReducer<{
  requestId: number;
  id: number | string;
}> = (state, action) => {
  state.activeMutations[String(action.payload.id)] = {
    requestId: action.payload.requestId,
    changesAfterStart: {},
  };
};

export const completeWorkItemMutationReducer: WorkItemCaseReducer<{
  requestId: number;
  workItem: IWorkItem;
}> = (state, action) => {
  if (isCompleteWorkItem(action.payload.workItem)) {
    completeTrackedWorkItemMutation(
      state,
      action.payload.requestId,
      action.payload.workItem
    );
  }
};

export const failWorkItemMutationReducer: WorkItemCaseReducer<{
  requestId: number;
  id: number | string;
}> = (state, action) => {
  const key = String(action.payload.id);
  if (state.activeMutations[key]?.requestId === action.payload.requestId) {
    delete state.activeMutations[key];
  }
};

export const reconcileWorkItemReducer: WorkItemCaseReducer<IWorkItem> = (
  state,
  action
) => {
  if (isCompleteWorkItem(action.payload)) {
    reconcileCompleteWorkItem(state, action.payload);
  }
};

export const reconcileCreatedWorkItemReducer: WorkItemCaseReducer<IWorkItem> = (
  state,
  action
) => {
  if (isCompleteWorkItem(action.payload)) {
    reconcileCreatedWorkItem(state, action.payload);
  }
};

export const resetWorkItemReconciliationReducer: CaseReducer<WorkItemState> = (
  state
) => {
  state.activeFetches = {};
  state.loadedScopes = {};
  state.activeMutations = {};
};
